import type { ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import type { SelectOption } from '../../components';
import { Select, SmartSearch } from '../../components';
import type { Installer } from '../../model';
import { DeleteInstaller } from './delete-installer';
import { useInstallers } from './graphql';
import { UpsertInstallerModal } from './modal';
import { Pagination } from './pagination';
import { Table } from './table';

type SortKey = 'newest' | 'oldest' | 'software';

const SORT_OPTIONS: SelectOption<SortKey>[] = [
  { value: 'newest', label: 'Date added (newest)' },
  { value: 'oldest', label: 'Date added (oldest)' },
  { value: 'software', label: 'Software' }
];

const SORT_MAP: Record<SortKey, { sort_field: string; order: string }> = {
  newest: { sort_field: 'date_created', order: 'DESC' },
  oldest: { sort_field: 'date_created', order: 'ASC' },
  software: { sort_field: 'app', order: 'ASC' }
};

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  color: rgb(159, 176, 255);
  background: rgba(78, 95, 253, 0.1);
  border: 1px solid rgba(120, 130, 255, 0.35);
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    background: rgba(78, 95, 253, 0.18);
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  > * {
    flex-shrink: 0;
  }
`;

const SearchBox = styled.div`
  width: 250px;
`;

export const Installers = (): ReactElement => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [currentPage, setPage] = useState(1);
  const [modal, setModal] = useState<'create' | 'delete' | 'update'>();
  const [installer, setInstaller] = useState<Installer>();

  const { sort_field, order } = SORT_MAP[sortKey];

  const { installers, loading, totalPages, refetch } = useInstallers({
    pagination: { page: currentPage, sort_field, order },
    extraFilters: { wildcard: search }
  });

  const applySort = (key: SortKey): void => {
    setSortKey(key);
    setPage(1);
  };

  return (
    <div>
      <TopRow>
        <AddButton
          type='button'
          onClick={(): void => {
            setModal('create');
          }}
        >
          <i className='far fa-plus-circle' /> Add Installer
        </AddButton>

        <FilterGroup>
          <Select icon='fas fa-sort' value={sortKey} options={SORT_OPTIONS} onChange={applySort} />
          <SearchBox>
            <SmartSearch
              search={search}
              onSearch={(value): void => {
                setSearch(value);
                setPage(1);
              }}
            />
          </SearchBox>
        </FilterGroup>
      </TopRow>

      <Table
        installers={installers}
        onUpdate={(installer_): void => {
          setInstaller(installer_);
          setModal('update');
        }}
        onDelete={(installer_): void => {
          setInstaller(installer_);
          setModal('delete');
        }}
      />

      <Pagination page={currentPage} changePage={setPage} totalPages={totalPages} loading={loading} />

      {modal && ['update', 'create'].includes(modal) && (
        <UpsertInstallerModal
          refetch={refetch}
          installer={installer}
          show={['update', 'create'].includes(modal)}
          onClose={(): void => {
            setModal(undefined);
            setInstaller(undefined);
          }}
        />
      )}
      {modal === 'delete' && (
        <DeleteInstaller
          installerId={installer?.id}
          refetch={refetch}
          onClose={(): void => {
            setModal(undefined);
            setInstaller(undefined);
          }}
        />
      )}
    </div>
  );
};
